import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download, MapPin } from "lucide-react";
import { HERO_LINES, PROFILE, INTRO } from "../data/content";
import { Headshot } from "./Headshot";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.3 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#EFEBDE] flex items-end"
    >
      {/* parallax redwood background */}
      <motion.div style={{ y: yImg }} className="absolute inset-0 -z-0">
        <img
          src="https://images.unsplash.com/photo-1562640843-214e78d87d83?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="w-full h-[130%] object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#EFEBDE] via-[#EFEBDE]/85 to-[#EFEBDE]/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#EFEBDE]/70 via-transparent to-[#EFEBDE]/40" />
      </motion.div>

      {/* top-right meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="absolute top-28 right-6 md:right-12 text-right hidden sm:block"
      >
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#4C5C50] flex items-center justify-end gap-2">
          <MapPin size={13} className="text-[#2C5F3A]" /> {PROFILE.location}
        </p>
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#66766A] mt-2">
          All 50 States · Remote
        </p>
      </motion.div>

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-12 pb-16 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-[#2C5F3A] mb-6 md:mb-8"
        >
          {PROFILE.role}
        </motion.p>

        <h1 className="font-display font-bold uppercase leading-[0.82] tracking-tighter text-[19vw] sm:text-[17vw] lg:text-[15vw] text-[#16281C]">
          {HERO_LINES.map((line, i) => (
            <span key={line} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 md:mt-12 grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-16 items-end">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1 }}
              className="max-w-xl font-body text-base md:text-lg text-[#4C5C50] leading-relaxed border-l-2 border-[#2C5F3A] pl-5"
            >
              {INTRO}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 1 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-3 bg-[#2C5F3A] text-[#EFEBDE] font-mono text-xs tracking-[0.2em] uppercase px-8 py-5 hover:bg-[#367A49] transition-colors"
              >
                Start a Project
                <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform" />
              </a>
              <a
                href={PROFILE.resumeUrl}
                download
                data-testid="hero-resume"
                className="group inline-flex items-center gap-3 border border-[#16281C]/25 text-[#16281C] font-mono text-xs tracking-[0.2em] uppercase px-8 py-5 hover:border-[#2C5F3A] hover:text-[#2C5F3A] transition-colors"
              >
                <Download size={16} /> Download Résumé
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:block w-[240px] shrink-0"
          >
            <Headshot className="w-full aspect-[3/4]" testid="hero-headshot" eager />
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#66766A]">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="w-px h-8 bg-gradient-to-b from-[#2C5F3A] to-transparent"
        />
      </motion.div>
    </section>
  );
};
